import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  RefreshCw,
  Loader2,
  User,
  Copy,
  ExternalLink,
  ChevronDown,
  Check,
  History,
  Wallet,
  Home,
  Globe,
  Plus,
  Trash2,
  Download
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'main', name: 'Главная', icon: Home, description: 'Баланс и новости' },
  { id: 'payments', name: 'Платежи', icon: History, description: 'Ввод / Вывод' },
  { id: 'personal', name: 'Профиль', icon: User, description: 'Персональные данные' }
];

// Email templates for crypto withdrawal
const EMAIL_TEMPLATES = [
  "Здравствуйте! Я занимался выводом криптовалюты через ваш сервис в течение последних 3 месяцев. Все операции проходили успешно, хочу продолжить сотрудничество. Готов предоставить дополнительные документы для верификации.",
  "Добрый день! Подтверждаю, что использовал вашу платформу для вывода USDT и BTC. Суммы были различные, от 1000 до 5000 долларов. Прошу рассмотреть мою заявку на повышение лимитов.",
  "Здравствуйте! Я активный пользователь вашего сервиса для вывода криптовалюты. За последний год провел более 50 успешных транзакций. Интересует долгосрочное сотрудничество.",
  "Добрый день! Обращаюсь как постоянный клиент, который регулярно выводит криптовалюту через вашу платформу. Все операции были успешными, без задержек. Хочу обсудить условия VIP-обслуживания."
];

export default function NovapayTemplate() {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('main');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showBrowser, setShowBrowser] = useState(true);
  const [shuffleTransactions, setShuffleTransactions] = useState(false);
  const [includeOzonProfile, setIncludeOzonProfile] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('');
  const [emailTemplateIndex, setEmailTemplateIndex] = useState(0);
  const [isDownloadingArchive, setIsDownloadingArchive] = useState(false);
  
  // IP login history
  // Generate initial email template

  const [ipLogins, setIpLogins] = useState([
    { ip: '', city: '', country: '', time: '' }
  ]);
  const [isLookingUpIP, setIsLookingUpIP] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ozon/profiles`, {
        headers: getAuthHeaders(),
      });
      setProfiles(response.data);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      toast.error("Ошибка загрузки профилей");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // Extract IP from proxy format: http://user:pass@IP:port
  const extractIPFromProxy = (input) => {
    if (!input) return null;
    
    // Try to parse proxy format: http://user:pass@IP:port
    const proxyMatch = input.match(/@([\d.]+):/);
    if (proxyMatch) {
      return proxyMatch[1];
    }
    
    // Try simpler format: IP:port
    const ipPortMatch = input.match(/^([\d.]+):\d+$/);
    if (ipPortMatch) {
      return ipPortMatch[1];
    }
    
    // Check if it's just an IP address
    const ipMatch = input.match(/^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})$/);
    if (ipMatch) {
      return ipMatch[1];
    }
    
    return null;
  };

  // Lookup IP geolocation
  const lookupIP = async (index) => {
    const rawInput = ipLogins[index].ip.trim();
    if (!rawInput) {
      toast.error("Введите IP адрес или прокси");
      return;
    }

    // Extract IP from proxy format
    const ip = extractIPFromProxy(rawInput);
    if (!ip) {
      toast.error("Неверный формат. Используйте: http://user:pass@IP:port или просто IP");
      return;
    }

    setIsLookingUpIP(prev => ({ ...prev, [index]: true }));
    
    try {
      // Use ipwho.is for geolocation (free, HTTPS supported)
      const response = await fetch(`https://ipwho.is/${ip}?lang=ru`);
      const data = await response.json();
      
      if (data.success !== false) {
        const newLogins = [...ipLogins];
        let city = data.city || 'Неизвестно';
        const country = data.country || 'Неизвестно';
        const region = data.region || '';
        
        // Map Moscow region to Moscow city
        if (region.toLowerCase().includes('moscow') || 
            region.toLowerCase().includes('москва') ||
            region.toLowerCase().includes('московская')) {
          city = 'Москва';
        }
        // Map St. Petersburg region
        if (region.toLowerCase().includes('petersburg') || 
            region.toLowerCase().includes('петербург') ||
            region.toLowerCase().includes('ленинград')) {
          city = 'Санкт-Петербург';
        }
        
        newLogins[index] = {
          ip: ip,
          city: city,
          country: country,
          region: region,
          time: generateRandomTime()
        };
        setIpLogins(newLogins);
        toast.success(`Город: ${city}, ${country}`);
      } else {
        toast.error("Не удалось определить местоположение IP");
      }
    } catch (error) {
      console.error("IP lookup error:", error);
      toast.error("Ошибка определения IP. Проверьте соединение.");
    } finally {
      setIsLookingUpIP(prev => ({ ...prev, [index]: false }));
    }
  };

  const generateRandomTime = () => {
    // Return current time when page is generated
    const now = new Date();
    return now.toLocaleString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const addIPLogin = () => {
    if (ipLogins.length >= 1) {
      toast.error("Только 1 запись для последнего входа");
      return;
    }
    setIpLogins([...ipLogins, { ip: '', city: '', country: '', time: '' }]);
  };

  const removeIPLogin = (index) => {
    if (ipLogins.length <= 1) return;
    setIpLogins(ipLogins.filter((_, i) => i !== index));
  };

  const updateIPLogin = (index, field, value) => {
    const newLogins = [...ipLogins];
    newLogins[index] = { ...newLogins[index], [field]: value };
    setIpLogins(newLogins);
  };

  const formatPhone = (phone) => {
    if (!phone) return "+7 (999) 123-45-67";
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11) {
      return `+${digits[0]} (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7,9)}-${digits.slice(9,11)}`;
    }
    return phone;
  };

  const generateEmailTemplate = useCallback(() => {
    const index = Math.floor(Math.random() * EMAIL_TEMPLATES.length);
    setEmailTemplateIndex(index);
    setEmailTemplate(EMAIL_TEMPLATES[index]);
  }, []);

  // Generate initial email template on mount
  useEffect(() => {
    generateEmailTemplate();
  }, [generateEmailTemplate]);

  const handleGenerate = () => {
    if (!selectedProfile) {
      toast.error("Выберите профиль OZON");
      return;
    }
    
    setIsGenerating(true);
    
    const firstName = selectedProfile.first_name || selectedProfile.name?.split(' ')[0] || "Имя";
    const lastName = selectedProfile.last_name || selectedProfile.name?.split(' ')[1] || "Фамилия";
    const patronymic = selectedProfile.patronymic || "";
    const phone = selectedProfile.phone || "+7 (999) 123-45-67";
    const email = selectedProfile.email !== "не установлен" ? selectedProfile.email : `${firstName.toLowerCase()}.${lastName.toLowerCase()}@mail.ru`;
    
    // Sort transactions by date (newest first)
    const sortedTransactions = [...(selectedProfile.transactions || [])].sort((a, b) => {
      const parseDate = (dateStr, timeStr) => {
        if (!dateStr) return 0;
        const parts = dateStr.split('.');
        if (parts.length === 3) {
          const [day, month, year] = parts;
          const time = timeStr || '00:00:00';
          return new Date(`${year}-${month}-${day}T${time}`).getTime();
        }
        return 0;
      };
      return parseDate(b.date, b.time) - parseDate(a.date, a.time);
    });

    // Map transactions for crypto
    let transactions = sortedTransactions.map(tx => ({
      ...tx,
      crypto: ['USDT', 'BTC', 'ETH', 'USDC'][Math.abs(tx.amount || 0) % 4]
    }));
    
    // Add random operations if enabled
    if (shuffleTransactions) {
      // Generate 5-6 random operations
      const numOps = 5 + Math.floor(Math.random() * 2); // 5 or 6
      const randomOps = [];
      
      // Calculate date range from transactions
      let dateFrom = null;
      let dateTo = null;
      if (transactions.length > 0) {
        const validDates = transactions
          .map(tx => {
            if (!tx.date) return null;
            const parts = tx.date.split('.');
            if (parts.length !== 3) return null;
            const [day, month, year] = parts;
            return { 
              original: tx.date, 
              sortValue: parseInt(year) * 10000 + parseInt(month) * 100 + parseInt(day) 
            };
          })
          .filter(d => d !== null);
        
        if (validDates.length > 0) {
          validDates.sort((a, b) => a.sortValue - b.sortValue);
          dateFrom = validDates[0].original;
          dateTo = validDates[validDates.length - 1].original;
        }
      }
      
      // Helper to generate random date
      const generateRandomDate = () => {
        if (!dateFrom || !dateTo) {
          const today = new Date();
          return today.toLocaleDateString('ru-RU').replace(/\//g, '.');
        }
        const fromParts = dateFrom.split('.');
        const toParts = dateTo.split('.');
        const fromDate = new Date(fromParts[2], fromParts[1] - 1, fromParts[0]);
        const toDate = new Date(toParts[2], toParts[1] - 1, toParts[0]);
        const randomTime = fromDate.getTime() + Math.random() * (toDate.getTime() - fromDate.getTime());
        const randomDate = new Date(randomTime);
        const day = String(randomDate.getDate()).padStart(2, '0');
        const month = String(randomDate.getMonth() + 1).padStart(2, '0');
        const year = randomDate.getFullYear();
        return `${day}.${month}.${year}`;
      };
      
      // Helper to generate random time
      const genRandomTime = () => {
        const hour = String(Math.floor(Math.random() * 24)).padStart(2, '0');
        const minute = String(Math.floor(Math.random() * 60)).padStart(2, '0');
        return `${hour}:${minute}`;
      };
      
      const randomNames = [
        'Иван Петрович М.', 'Алексей Сергеевич К.', 'Мария Николаевна С.',
        'Дмитрий Александрович В.', 'Елена Владимировна П.', 'Сергей Михайлович Л.'
      ];
      
      for (let i = 0; i < numOps; i++) {
        const amount = 2000 + Math.floor(Math.random() * 8001); // 2000-10000
        const type = Math.random() > 0.5 ? 'deposit' : 'withdrawal';
        
        randomOps.push({
          id: `random-${Date.now()}-${i}`,
          type,
          amount,
          method: randomNames[Math.floor(Math.random() * randomNames.length)],
          date: generateRandomDate(),
          time: genRandomTime(),
          category: type === 'deposit' ? 'Пополнения' : 'Переводы',
          crypto: ['USDT', 'BTC', 'ETH', 'USDC'][Math.floor(Math.random() * 4)],
          isRandom: true
        });
      }
      
      // Combine transactions
      transactions = [...transactions, ...randomOps];
      
      // Sort all by date/time (newest first)
      transactions.sort((a, b) => {
        const parseToSortValue = (dateStr, timeStr) => {
          if (!dateStr) return 0;
          const parts = dateStr.split('.');
          if (parts.length !== 3) return 0;
          const [day, month, year] = parts;
          const time = timeStr || '00:00';
          const [hour, min] = time.split(':').map(Number);
          return parseInt(year) * 100000000 + parseInt(month) * 1000000 + parseInt(day) * 10000 + (hour || 0) * 100 + (min || 0);
        };
        return parseToSortValue(b.date, b.time) - parseToSortValue(a.date, a.time);
      });
      
      // ALWAYS add first deposit (positive) at the bottom with 2000-10000
      // In ONESIX display: OZON deposit → ONESIX withdrawal, OZON withdrawal → ONESIX deposit (Ввод)
      // So to show "Ввод" (deposit in display), we need type: 'withdrawal'
      if (transactions.length > 0) {
        const lastTx = transactions[transactions.length - 1];
        const oldestDate = lastTx.date;
        const oldestParts = oldestDate.split('.');
        const oldestDateObj = new Date(oldestParts[2], oldestParts[1] - 1, oldestParts[0]);
        oldestDateObj.setDate(oldestDateObj.getDate() - (1 + Math.floor(Math.random() * 3)));
        const day = String(oldestDateObj.getDate()).padStart(2, '0');
        const month = String(oldestDateObj.getMonth() + 1).padStart(2, '0');
        const year = oldestDateObj.getFullYear();
        
        const firstDeposit = {
          id: `first-deposit-${Date.now()}`,
          type: 'withdrawal',  // withdrawal in data → deposit (Ввод) in ONESIX display
          amount: 2000 + Math.floor(Math.random() * 8001),
          method: 'Первый перевод СБП',
          date: `${day}.${month}.${year}`,
          time: genRandomTime(),
          category: 'Пополнения',
          crypto: 'USDT',
          isFirstDeposit: true
        };
        
        transactions.push(firstDeposit);
        // Re-sort
        transactions.sort((a, b) => {
          const parseToSortValue = (dateStr, timeStr) => {
            if (!dateStr) return 0;
            const parts = dateStr.split('.');
            if (parts.length !== 3) return 0;
            const [day, month, year] = parts;
            const time = timeStr || '00:00';
            const [hour, min] = time.split(':').map(Number);
            return parseInt(year) * 100000000 + parseInt(month) * 1000000 + parseInt(day) * 10000 + (hour || 0) * 100 + (min || 0);
          };
          return parseToSortValue(b.date, b.time) - parseToSortValue(a.date, a.time);
        });
      }
    }
    
    // Calculate date range from final transactions
    let finalDateFrom = null;
    let finalDateTo = null;
    if (transactions.length > 0) {
      const validDates = transactions
        .map(tx => {
          if (!tx.date) return null;
          const parts = tx.date.split('.');
          if (parts.length !== 3) return null;
          const [day, month, year] = parts;
          return { 
            original: tx.date, 
            sortValue: parseInt(year) * 10000 + parseInt(month) * 100 + parseInt(day) 
          };
        })
        .filter(d => d !== null);
      
      if (validDates.length > 0) {
        validDates.sort((a, b) => a.sortValue - b.sortValue);
        finalDateFrom = validDates[0].original;
        finalDateTo = validDates[validDates.length - 1].original;
      }
    }
    
    const data = {
      id: selectedProfile.id,
      novapayId: selectedProfile.ozon_id || Math.floor(100000 + Math.random() * 900000),
      firstName,
      lastName,
      patronymic,
      fullName: `${firstName} ${lastName}${patronymic ? " " + patronymic : ""}`,
      phone: formatPhone(phone),
      email,
      balance: selectedProfile.template_balance || selectedProfile.balance || 0,
      transactions,
      ipLogins: ipLogins.filter(l => l.ip && l.city),
      verified: true,
      showBrowser,
      dateFrom: finalDateFrom,
      dateTo: finalDateTo
    };
    
    setGeneratedData(data);
    setIsGenerating(false);
    toast.success("Данные сгенерированы!");
  };

  const downloadArchive = async () => {
    if (!generatedData) return;
    
    setIsDownloadingArchive(true);
    toast.info("Создание архива скриншотов...");
    
    try {
      const response = await axios.post(
        `${API}/templates/novapay/archive`,
        { 
          data: generatedData,
          pages: ['main', 'payments', 'personal'],
          includeOzonProfile: includeOzonProfile,
          ozonProfile: includeOzonProfile ? selectedProfile : null
        },
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ONESIX_${generatedData.novapayId}_${Date.now()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Архив скриншотов скачан!");
    } catch (error) {
      console.error("Archive error:", error);
      toast.error("Ошибка создания архива");
    } finally {
      setIsDownloadingArchive(false);
    }
  };

  const getPreviewUrl = () => {
    if (!generatedData) return '';
    const dataStr = encodeURIComponent(JSON.stringify(generatedData));
    return `${window.location.origin}/templates/novapay/${selectedTemplate}?data=${dataStr}`;
  };

  const openPreview = () => {
    const url = getPreviewUrl();
    if (url) {
      window.open(url, '_blank');
    }
  };

  const copyLink = () => {
    const url = getPreviewUrl();
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success("Ссылка скопирована!");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="novapay-template-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <Wallet className="w-5 h-5 text-white" />
            </div>
            Крипто ONESIX
          </h1>
          <p className="text-slate-500 mt-1">
            Генератор криптовалютного кошелька из профиля OZON
          </p>
        </div>
        <Button 
          onClick={() => fetchProfiles()}
          variant="outline"
          data-testid="refresh-profiles-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Обновить профили
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Settings */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Настройки генерации</h2>
            
            {/* Template Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Выберите страницу</Label>
              <div className="grid grid-cols-3 gap-3">
                {TEMPLATES.map((template) => {
                  const Icon = template.icon;
                  return (
                    <button
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplate(template.id);
                        setGeneratedData(null);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedTemplate === template.id
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mb-2 ${
                        selectedTemplate === template.id ? 'text-violet-500' : 'text-slate-400'
                      }`} />
                      <div className={`font-medium ${
                        selectedTemplate === template.id ? 'text-violet-700' : 'text-slate-700'
                      }`}>
                        {template.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{template.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Show Browser Option */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-violet-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={showBrowser}
                  onChange={(e) => {
                    setShowBrowser(e.target.checked);
                    setGeneratedData(null);
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-violet-500 focus:ring-violet-500"
                />
                <div>
                  <div className="font-medium text-slate-700">Показывать рамку браузера</div>
                  <div className="text-xs text-slate-500">
                    Отображать Chrome-рамку с вкладками и адресной строкой
                  </div>
                </div>
              </label>
            </div>

            {/* Random Operations Option */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-violet-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={shuffleTransactions}
                  onChange={(e) => {
                    setShuffleTransactions(e.target.checked);
                    setGeneratedData(null);
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-violet-500 focus:ring-violet-500"
                />
                <div>
                  <div className="font-medium text-slate-700">Рандомные операции</div>
                  <div className="text-xs text-slate-500">
                    Добавить 5-6 случайных операций, первая снизу положительная
                  </div>
                </div>
              </label>
            </div>


            {/* Include OZON Profile in Archive */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-violet-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={includeOzonProfile}
                  onChange={(e) => {
                    setIncludeOzonProfile(e.target.checked);
                    setGeneratedData(null);
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-violet-500 focus:ring-violet-500"
                />
                <div>
                  <div className="font-medium text-slate-700">Добавить профиль Ozon</div>
                  <div className="text-xs text-slate-500">
                    Включить данные профиля Ozon и фото в архив
                  </div>
                </div>
              </label>
            </div>
            {/* Profile Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Профиль OZON</Label>
              <div className="relative">
                <button
                  data-testid="profile-dropdown"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full p-3 rounded-lg border-2 border-slate-200 bg-white text-left flex items-center justify-between hover:border-slate-300 transition-colors"
                >
                  {selectedProfile ? (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-violet-600" />
                      </div>
                      <div>
                        <div className="text-slate-800 font-medium">{selectedProfile.name}</div>
                        <div className="text-xs text-slate-500">
                          ID: {selectedProfile.ozon_id} • Баланс: {(selectedProfile.template_balance || selectedProfile.balance || 0).toLocaleString()} ₽
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-400">Выберите профиль...</span>
                  )}
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute z-50 w-full mt-2 py-2 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                    {profiles.length === 0 ? (
                      <div className="px-4 py-3 text-slate-400 text-center">
                        Нет профилей
                      </div>
                    ) : (
                      profiles.map((profile) => (
                        <button
                          key={profile.id}
                          onClick={() => {
                            setSelectedProfile(profile);
                            setDropdownOpen(false);
                            setGeneratedData(null);
                          }}
                          className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors ${
                            selectedProfile?.id === profile.id ? 'bg-violet-50' : ''
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                          <div className="text-left flex-1">
                            <div className="text-slate-800">{profile.name}</div>
                            <div className="text-xs text-slate-500">
                              ID: {profile.ozon_id} • {(profile.template_balance || profile.balance || 0).toLocaleString()} ₽
                            </div>
                          </div>
                          {selectedProfile?.id === profile.id && (
                            <Check className="w-4 h-4 text-violet-500" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <Button
              data-testid="generate-btn"
              onClick={handleGenerate}
              disabled={!selectedProfile || isGenerating}
              className="w-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white gap-2"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Wallet className="w-4 h-4" />
              )}
              Сформировать страницу
            </Button>
          </CardContent>

        {/* Right Panel - Preview Info */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Предпросмотр</h2>
            
            {generatedData ? (
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-200">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xl font-semibold text-slate-800">{generatedData.fullName}</div>
                    <div className="text-violet-600 font-medium flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                        <Check className="w-3 h-3" /> Верифицирован
                      </span>
                      <span>ID: {generatedData.novapayId}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {selectedTemplate === 'payments' && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <History className="w-4 h-4 text-slate-400" />
                      <span>{generatedData.transactions.length} транзакций</span>
                    </div>
                  )}
                  {selectedTemplate === 'personal' && generatedData.ipLogins?.length > 0 && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <span>{generatedData.ipLogins.length} записей входа</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    data-testid="open-preview-btn"
                    onClick={openPreview}
                    className="flex-1 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Открыть
                  </Button>
                  <Button
                    data-testid="copy-link-btn"
                    variant="outline"
                    onClick={copyLink}
                    className="gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Копировать
                  </Button>
                </div>
                
                {/* Download Archive Button */}
                <Button 
                  onClick={downloadArchive}
                  disabled={isDownloadingArchive}
                  variant="outline"
                  className="w-full mt-3"
                  data-testid="download-archive-btn"
                >
                  {isDownloadingArchive ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Скачать архив
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-200 to-blue-200 rounded-2xl flex items-center justify-center mb-4">
                  <Wallet className="w-8 h-8 text-violet-400" />
                </div>
                <div className="text-slate-400">
                  Выберите профиль и нажмите<br />"Сформировать страницу"
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        </Card>
      </div>
    </div>
  );
}
